import React, { createContext, useContext, useRef } from 'react';
import { UseMutateAsyncFunction, useMutation } from 'react-query';

import { Config } from '@nw/shared/config';

import {
  RefreshAuthTokensRequestType,
  RefreshAuthTokensResponseType
} from '../services/PaymentsAuthServices/APIService/types';
import {
  clearAllPaymentsDomainTokens,
  getPaymentAccessToken,
  getPaymentRefreshToken,
  setPaymentsAccessTokenToDomain,
  setPaymentsRefreshTokenToDomain
} from '../utils/PaymentStorageUtils';
import { clearUserSessionAndReload } from '../utils/UserSessionUtils';

import { useGetClientAuthCodeAPI } from './IbUserAccounts/api';
import { useGetPaymentsClientAuthDetails } from './PaymentsAuth/api';
import { usePaymentsAuthRefreshTokenAPIService } from './PaymentsAuth/usePaymentsAuthRefreshTokenAPIService';

interface PaymentsRefreshAuthTokensProviderProps {
  children: React.ReactNode;
}

interface PaymentsRefreshAuthTokensContextTypes {
  refreshTokenApi: UseMutateAsyncFunction<
    RefreshAuthTokensResponseType,
    Error,
    RefreshAuthTokensRequestType
  >;
  getAccessToken: () => string;
  getRefreshToken: () => string;
}

export const PaymentsRefreshAuthTokensContext =
  createContext<PaymentsRefreshAuthTokensContextTypes | null>(null);

export const PaymentsRefreshAuthTokenProvider: React.FC<
  PaymentsRefreshAuthTokensProviderProps
> = (props) => {
  const refreshTokenPromise = useRef<Promise<RefreshAuthTokensResponseType>>();

  const { paymentsAuthRefreshTokenAPIService } =
    usePaymentsAuthRefreshTokenAPIService();
  const { getClientAuthCodeAPI } = useGetClientAuthCodeAPI();
  const { getPaymentsClientAuthDetailsAPI } = useGetPaymentsClientAuthDetails();

  const { mutateAsync: refreshAuthTokensApi } = useMutation(
    async (requestObject: RefreshAuthTokensRequestType) => {
      refreshTokenPromise.current = new Promise<RefreshAuthTokensResponseType>(
        async (resolve, reject) => {
          try {
            if (requestObject.refresh_token) {
              const data =
                await paymentsAuthRefreshTokenAPIService.getRefreshToken(
                  requestObject
                );

              clearAllPaymentsDomainTokens();

              setPaymentsAccessTokenToDomain(data.access_token);
              setPaymentsRefreshTokenToDomain(data.refresh_token);
              resolve(data);
            } else {
              throw new Error(
                JSON.stringify({
                  response: 'Invalid refresh token',
                  http_status_code: 400,
                  res_status: 'REFRESH_TOKEN_EXPIRED'
                })
              );
            }
          } catch (e) {
            const errorResponse = JSON.parse(e.message);
            if (errorResponse.res_status === 'REFRESH_TOKEN_EXPIRED') {
              try {
                const authCodeResponse = await getClientAuthCodeAPI({
                  client_id: Config['OTG_PAYMENTS_CLIENT_ID']
                });

                const clientAuthenticationDetails =
                  await getPaymentsClientAuthDetailsAPI({
                    client_id: Config['OTG_PAYMENTS_CLIENT_ID'],
                    code: authCodeResponse.code
                  });

                clearAllPaymentsDomainTokens();
                setPaymentsAccessTokenToDomain(
                  clientAuthenticationDetails.access_token
                );
                setPaymentsRefreshTokenToDomain(
                  clientAuthenticationDetails.refresh_token
                );
                resolve(clientAuthenticationDetails);
              } catch (error) {
                clearUserSessionAndReload();
                reject(error);
              }
            } else {
              clearUserSessionAndReload();
              reject(
                new Error(
                  JSON.stringify({
                    response: 'Failed to refresh authentication token',
                    http_status_code: 400,
                    res_status: 'REFRESH_TOKEN_ERROR'
                  })
                )
              );
            }
          }
        }
      );

      return refreshTokenPromise.current;
    }
  );

  return (
    <PaymentsRefreshAuthTokensContext.Provider
      value={{
        refreshTokenApi: refreshAuthTokensApi,
        getAccessToken: getPaymentAccessToken,
        getRefreshToken: getPaymentRefreshToken
      }}
    >
      {props.children}
    </PaymentsRefreshAuthTokensContext.Provider>
  );
};

export const usePaymentsRefreshAuthTokens =
  (): PaymentsRefreshAuthTokensContextTypes => {
    const paymentsRefreshAuthTokensContext = useContext(
      PaymentsRefreshAuthTokensContext
    );

    if (!paymentsRefreshAuthTokensContext)
      throw new Error(
        'Expected to be wrapped in an PaymentsRefreshAuthTokensProvider'
      );

    return paymentsRefreshAuthTokensContext;
  };
